import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveTable, ResponsiveTableColumn } from '@/components/common/ResponsiveTable';
import { Alert } from '@/components/common/Alert';
import { Badge } from '@/components/common/Badge';
import { promoCodeService } from '@/services/promo-code.service';
import { PromoCode, CreatePromoCodeDto } from '@/types/promo-code.types';
import '@/styles/dashboard.css';

const ITEMS_PER_PAGE = 10;

export const CodePromo = () => {
  const { t } = useTranslation();
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; message: string } | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState<CreatePromoCodeDto>({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 0,
    min_purchase_amount: undefined,
    max_discount_amount: undefined,
    usage_limit: undefined,
    start_date: '',
    end_date: '',
    is_active: true,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadPromoCodes();
  }, []);

  const loadPromoCodes = async () => {
    try {
      setLoading(true);
      const data = await promoCodeService.getAll();
      setPromoCodes(data);
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement des codes promo:', err);
      setError('Erreur lors du chargement des codes promo');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.code || formData.code.trim() === '') {
      errors.code = t('adminPromo.codeRequiredError');
    }

    if (!formData.discount_value || formData.discount_value <= 0) {
      errors.discount_value = t('adminPromo.valueRequiredError');
    }

    if (formData.discount_type === 'percentage' && formData.discount_value > 100) {
      errors.discount_value = t('adminPromo.percentMaxError');
    }

    if (formData.start_date && formData.end_date) {
      if (new Date(formData.start_date) > new Date(formData.end_date)) {
        errors.end_date = t('adminPromo.endDateError');
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (editingId) {
        await promoCodeService.update(editingId, formData);
        alert(t('adminPromo.updateSuccess'));
      } else {
        await promoCodeService.create(formData);
        alert(t('adminPromo.createSuccess'));
      }
      resetForm();
      await loadPromoCodes();
    } catch (err: any) {
      console.error('Erreur:', err);
      const errorMessage = err?.response?.data?.error || err?.message || 'Erreur lors de la sauvegarde';
      alert(errorMessage);
    }
  };

  const handleEdit = (promoCode: PromoCode) => {
    setFormData({
      code: promoCode.code,
      description: promoCode.description || '',
      discount_type: promoCode.discount_type,
      discount_value: promoCode.discount_value,
      min_purchase_amount: promoCode.min_purchase_amount || undefined,
      max_discount_amount: promoCode.max_discount_amount || undefined,
      usage_limit: promoCode.usage_limit || undefined,
      start_date: promoCode.start_date ? promoCode.start_date.split('T')[0] : '',
      end_date: promoCode.end_date ? promoCode.end_date.split('T')[0] : '',
      is_active: promoCode.is_active,
    });
    setEditingId(promoCode.id);
    setShowForm(true);
  };

  const handleDelete = async (id: number, code: string) => {
    if (!window.confirm(t('adminPromo.deleteConfirm', { code }))) {
      return;
    }

    try {
      await promoCodeService.delete(id);
      setNotification({ type: 'success', message: t('adminPromo.deleteSuccess') });
      await loadPromoCodes();
    } catch (err: any) {
      console.error('Erreur:', err);
      setNotification({ type: 'error', message: err?.response?.data?.error || 'Erreur lors de la suppression' });
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 0,
      min_purchase_amount: undefined,
      max_discount_amount: undefined,
      usage_limit: undefined,
      start_date: '',
      end_date: '',
      is_active: true,
    });
    setEditingId(null);
    setShowForm(false);
    setFormErrors({});
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const filteredPromoCodes = useMemo(() => {
    let filtered = [...promoCodes];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(promo =>
        promo.code.toLowerCase().includes(query) ||
        promo.description?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [promoCodes, searchQuery]);

  const totalPages = Math.ceil(filteredPromoCodes.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedPromoCodes = filteredPromoCodes.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const getStatusBadge = (promoCode: PromoCode) => {
    const now = new Date();
    const startDate = promoCode.start_date ? new Date(promoCode.start_date) : null;
    const endDate = promoCode.end_date ? new Date(promoCode.end_date) : null;

    if (!promoCode.is_active) {
      return <Badge variant="neutral">{t('adminPromo.inactive')}</Badge>;
    }

    if (startDate && startDate > now) {
      return <Badge variant="warning">{t('adminPromo.coming')}</Badge>;
    }

    if (endDate && endDate < now) {
      return <Badge variant="error">{t('adminPromo.expired')}</Badge>;
    }

    if (promoCode.usage_limit && promoCode.usage_count >= promoCode.usage_limit) {
      return <Badge variant="error">{t('adminPromo.depleted')}</Badge>;
    }

    return <Badge variant="success">{t('adminPromo.active')}</Badge>;
  };

  const columns: ResponsiveTableColumn<PromoCode>[] = [
    {
      key: 'code',
      label: t('adminPromo.code'),
      render: (promoCode) => (
        <div className="table-cell-name" style={{ fontFamily: 'monospace' }}>
          {promoCode.code}
        </div>
      ),
    },
    {
      key: 'description',
      label: t('adminPromo.description'),
      render: (promoCode) => (
        <div className="table-cell-text">{promoCode.description || '-'}</div>
      ),
      mobileHidden: true,
    },
    {
      key: 'type',
      label: 'Type',
      render: (promoCode) => (
        <div className="table-cell-text">
          {promoCode.discount_type === 'percentage' ? t('adminPromo.percentage') : t('adminPromo.fixedAmount')}
        </div>
      ),
      mobileHidden: true,
    },
    {
      key: 'value',
      label: 'Valeur',
      render: (promoCode) => (
        <div className="table-cell-number">
          {promoCode.discount_type === 'percentage'
            ? `${promoCode.discount_value}%`
            : `${promoCode.discount_value} €`}
          {promoCode.max_discount_amount && promoCode.discount_type === 'percentage' && (
            <div className="text-xs text-body" style={{ marginTop: '4px' }}>
              {t('adminPromo.max')} {promoCode.max_discount_amount} €
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'usage',
      label: t('adminPromo.usages'),
      render: (promoCode) => (
        <div className="table-cell-number">
          {promoCode.usage_limit
            ? `${promoCode.usage_count} / ${promoCode.usage_limit}`
            : `${promoCode.usage_count} (${t('adminPromo.unlimited')})`}
        </div>
      ),
      mobileHidden: true,
    },
    {
      key: 'dates',
      label: t('adminPromo.dates'),
      render: (promoCode) => (
        <div className="table-cell-text" style={{ fontSize: '12px' }}>
          <div>{t('adminPromo.start')} {formatDate(promoCode.start_date)}</div>
          <div>{t('adminPromo.end')} {formatDate(promoCode.end_date)}</div>
        </div>
      ),
      mobileHidden: true,
    },
    {
      key: 'status',
      label: t('adminPromo.status'),
      render: (promoCode) => getStatusBadge(promoCode),
    },
    {
      key: 'actions',
      label: t('adminPromo.actions'),
      mobileHidden: true,
      render: (promoCode) => (
        <div className="product-actions">
          <button
            className="action-btn action-edit"
            onClick={() => handleEdit(promoCode)}
            title={t('adminPromo.edit')}
            aria-label={t('adminPromo.editPromoAria')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
              <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
            </svg>
          </button>
          <button
            className="action-btn action-delete"
            onClick={() => handleDelete(promoCode.id, promoCode.code)}
            title={t('adminPromo.delete')}
            aria-label={t('adminPromo.deletePromoAria')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
              <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, code }));
  };

  return (
    <>
    <div className="table-card">
              <div className="table-header">
                <h3 className="table-title">{t('adminPromo.title', { count: filteredPromoCodes.length })}</h3>
                <div className="flex gap-2 items-center">
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="btn btn-secondary btn-sm"
                    >
                      {t('adminPromo.resetFilters')}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      resetForm();
                      setShowForm(true);
                    }}
                    className="btn btn-primary btn-sm"
                  >
                    {t('adminPromo.addPromo')}
                  </button>
                </div>
              </div>

              {/* Filtres */}
              <div className="filters-bar">
                <div className="form-group-flex">
                  <label className="form-label">{t('adminPromo.search')}</label>
                  <input
                    type="text"
                    placeholder={t('adminPromo.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              {notification && (
                <Alert
                  type={notification.type}
                  message={notification.message}
                  onClose={() => setNotification(null)}
                />
              )}

              {error ? (
                <Alert type="error" message={error} />
              ) : (
                <ResponsiveTable
                  columns={columns}
                  data={paginatedPromoCodes}
                  loading={loading}
                  loadingMessage={t('adminPromo.loading')}
                  emptyMessage={
                    searchQuery
                      ? t('adminPromo.noSearch')
                      : t('adminPromo.empty')
                  }
                  mobileCardTitle={(promoCode) => (
                    <div>
                      <div className="table-cell-name mb-1" style={{ fontFamily: 'monospace' }}>
                        {promoCode.code}
                      </div>
                      <div className="table-cell-description">
                        {promoCode.description || t('adminPromo.noDescription')}
                      </div>
                    </div>
                  )}
                  mobileCardActions={(promoCode) => (
                    <div className="product-actions" style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="action-btn action-edit"
                        onClick={() => handleEdit(promoCode)}
                        title={t('adminPromo.edit')}
                        aria-label={t('adminPromo.editPromoAria')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                        </svg>
                      </button>
                      <button
                        className="action-btn action-delete"
                        onClick={() => handleDelete(promoCode.id, promoCode.code)}
                        title={t('adminPromo.delete')}
                        aria-label={t('adminPromo.deletePromoAria')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                        </svg>
                      </button>
                    </div>
                  )}
                  tableClassName="products-table"
                />
              )}

              {/* Pagination */}
              {!error && !loading && totalPages > 1 && (
                <div style={{
                  padding: '20px',
                  borderTop: '1px solid #e0e0e0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    {t('adminPromo.displaying', { start: startIndex + 1, end: Math.min(endIndex, filteredPromoCodes.length), total: filteredPromoCodes.length })}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        background: currentPage === 1 ? '#f5f5f5' : 'white',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        opacity: currentPage === 1 ? 0.5 : 1
                      }}
                    >
                      Précédent
                    </button>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
    <>

                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              style={{
                                padding: '8px 12px',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                background: currentPage === page ? '#8B204E' : 'white',
                                color: currentPage === page ? 'white' : '#333',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: currentPage === page ? 600 : 400
                              }}
                            >
                              {page}
                            </button>
                          
    </>
  );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} style={{ padding: '8px' }}>...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        background: currentPage === totalPages ? '#f5f5f5' : 'white',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        opacity: currentPage === totalPages ? 0.5 : 1
                      }}
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Formulaire */}
            {showForm && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                overflow: 'auto',
                padding: '20px'
              }}>
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  width: '90%',
                  maxWidth: '600px',
                  maxHeight: '90vh',
                  overflow: 'auto'
                }}>
                  <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: 600 }}>
                    {editingId ? t('adminPromo.modalEdit') : t('adminPromo.modalNew')}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>
                        {t('adminPromo.codeRequired')}
                      </label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="text"
                          value={formData.code}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }));
                            if (formErrors.code) setFormErrors(prev => ({ ...prev, code: '' }));
                          }}
                          placeholder={t('adminPromo.codePlaceholder')}
                          style={{
                            flex: 1,
                            padding: '10px 16px',
                            border: `1px solid ${formErrors.code ? '#f44336' : '#ddd'}`,
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontFamily: 'monospace',
                            textTransform: 'uppercase'
                          }}
                        />
                        <button
                          onClick={generateRandomCode}
                          type="button"
                          style={{
                            padding: '10px 16px',
                            background: '#f5f5f5',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {t('adminPromo.generate')}
                        </button>
                      </div>
                      {formErrors.code && <div className="error-message">{formErrors.code}</div>}
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>
                        {t('adminPromo.descriptionOptional')}
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder={t('adminPromo.descriptionPlaceholder')}
                        rows={2}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          fontSize: '14px',
                          resize: 'vertical'
                        }}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>
                          {t('adminPromo.discountType')}
                        </label>
                        <select
                          value={formData.discount_type}
                          onChange={(e) => setFormData(prev => ({ ...prev, discount_type: e.target.value as 'percentage' | 'fixed' }))}
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            fontSize: '14px'
                          }}
                        >
                          <option value="percentage">{t('adminPromo.percentageOption')}</option>
                          <option value="fixed">{t('adminPromo.fixedOption')}</option>
                        </select>
                      </div>

                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>
                          {t('adminPromo.valueLabel')}
                        </label>
                        <input
                          type="number"
                          value={formData.discount_value || ''}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) || 0;
                            setFormData(prev => ({ ...prev, discount_value: value }));
                            if (formErrors.discount_value) setFormErrors(prev => ({ ...prev, discount_value: '' }));
                          }}
                          placeholder={formData.discount_type === 'percentage' ? t('adminPromo.valuePercentagePlaceholder') : t('adminPromo.valueFixedPlaceholder')}
                          min="0"
                          max={formData.discount_type === 'percentage' ? '100' : undefined}
                          step="0.01"
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            border: `1px solid ${formErrors.discount_value ? '#f44336' : '#ddd'}`,
                            borderRadius: '8px',
                            fontSize: '14px'
                          }}
                        />
                        {formErrors.discount_value && <div className="error-message">{formErrors.discount_value}</div>}
                      </div>
                    </div>

                    {formData.discount_type === 'percentage' && (
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>
                          {t('adminPromo.maxDiscount')}
                        </label>
                        <input
                          type="number"
                          value={formData.max_discount_amount || ''}
                          onChange={(e) => {
                            const value = e.target.value ? parseFloat(e.target.value) : undefined;
                            setFormData(prev => ({ ...prev, max_discount_amount: value }));
                          }}
                          placeholder={t('adminPromo.maxDiscountPlaceholder')}
                          min="0"
                          step="0.01"
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>
                          {t('adminPromo.minPurchase')}
                        </label>
                        <input
                          type="number"
                          value={formData.min_purchase_amount || ''}
                          onChange={(e) => {
                            const value = e.target.value ? parseFloat(e.target.value) : undefined;
                            setFormData(prev => ({ ...prev, min_purchase_amount: value }));
                          }}
                          placeholder={t('adminPromo.minPurchasePlaceholder')}
                          min="0"
                          step="0.01"
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            fontSize: '14px'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>
                          {t('adminPromo.usageLimit')}
                        </label>
                        <input
                          type="number"
                          value={formData.usage_limit || ''}
                          onChange={(e) => {
                            const value = e.target.value ? parseInt(e.target.value) : undefined;
                            setFormData(prev => ({ ...prev, usage_limit: value }));
                          }}
                          placeholder={t('adminPromo.usageLimitPlaceholder')}
                          min="1"
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>
                          {t('adminPromo.startDate')}
                        </label>
                        <input
                          type="datetime-local"
                          value={formData.start_date || ''}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, start_date: e.target.value }));
                            if (formErrors.end_date) setFormErrors(prev => ({ ...prev, end_date: '' }));
                          }}
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            fontSize: '14px'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>
                          {t('adminPromo.endDate')}
                        </label>
                        <input
                          type="datetime-local"
                          value={formData.end_date || ''}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, end_date: e.target.value }));
                            if (formErrors.end_date) setFormErrors(prev => ({ ...prev, end_date: '' }));
                          }}
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            border: `1px solid ${formErrors.end_date ? '#f44336' : '#ddd'}`,
                            borderRadius: '8px',
                            fontSize: '14px'
                          }}
                        />
                        {formErrors.end_date && <div className="error-message">{formErrors.end_date}</div>}
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                        />
                        <span style={{ fontSize: '14px' }}>{t('adminPromo.promoActive')}</span>
                      </label>
                    </div>

                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
                      <button
                        onClick={resetForm}
                        type="button"
                        style={{
                          padding: '10px 20px',
                          background: '#f5f5f5',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 500
                        }}
                      >
                        {t('adminPromo.cancel')}
                      </button>
                      <button
                        onClick={handleSubmit}
                        type="button"
                        style={{
                          padding: '10px 20px',
                          background: '#8B204E',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 600
                        }}
                      >
                        {editingId ? t('adminPromo.update') : t('adminPromo.create')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
    </>
  );
};
